import React, { useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useDeleteData, useFetchData, useUpdateData } from '../../../hooks';
import { convertDate, dateFormat } from '../../../lib/utils';
import {
  ProderPlan,
  ProderPlanRequest,
  ProderProcessPlan,
} from '../../../types';
import ShipDecisionBlock from './ShipDecisionBlock';
import ReadinessBlock from './ReadinessBlock';

const { Text } = Typography;

/** Same rule as the server: the start is day 1, Sundays are skipped. */
const addWorkingDays = (start: Dayjs, days: number): Dayjs => {
  let d = start;
  let counted = 1;
  while (counted < days) {
    d = d.add(1, 'day');
    if (d.day() !== 0) counted += 1;
  }
  return d;
};

const hours = (minutes: number | null) =>
  minutes === null ? null : Math.round((minutes / 60) * 10) / 10;

interface Draft {
  workers: number | null;
  start: Dayjs | null;
  end: Dayjs | null;
  reason: string;
}

/**
 * Plan a proder per process (spec step 10). The server computes days and the
 * end; this preview uses the same formula so the planner sees the result
 * before saving, and the saved row is what the server returned.
 */
const ProderPlanSection: React.FC<{ proderNo: string }> = ({ proderNo }) => {
  const API = `/planning/proder-plan/${encodeURIComponent(proderNo)}`;
  const { data, isLoading, error } = useFetchData<ProderPlan>(API);
  const save = useUpdateData<ProderPlanRequest>(API);
  const remove = useDeleteData(API);
  const saveDue = useUpdateData<{ finalDue: string; note: string | null }>(API);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [dueDraft, setDueDraft] = useState<Dayjs | null>(null);

  if (isLoading) return <Spin />;
  if (error || !data) {
    return (
      <Alert type={'warning'} showIcon message={'The plan could not be loaded.'} />
    );
  }

  const minutesDay = data.minutesPerDay;

  const draftOf = (p: ProderProcessPlan): Draft =>
    drafts[p.process] ?? {
      workers: p.saved?.workersPlanned ?? p.suggestion.workers,
      start: dayjs(p.saved?.plannedStart ?? p.suggestion.start),
      end: p.saved?.endOverridden ? dayjs(p.saved.plannedEnd) : null,
      reason: p.saved?.overrideReason ?? '',
    };

  const setDraft = (p: ProderProcessPlan, patch: Partial<Draft>) =>
    setDrafts((all) => ({ ...all, [p.process]: { ...draftOf(p), ...patch } }));

  const workFor = (p: ProderProcessPlan) =>
    p.workBasis === 'REMAINING' ? p.workMinutesRemaining : p.workMinutesTotal;

  const preview = (p: ProderProcessPlan, d: Draft) => {
    const work = workFor(p);
    if (work === null || !minutesDay || !d.workers || d.workers <= 0 || !d.start) {
      return null;
    }
    const days = Math.max(1, Math.ceil(work / (d.workers * minutesDay)));
    return { days, end: addWorkingDays(d.start, days) };
  };

  const onSave = (p: ProderProcessPlan) => {
    const d = draftOf(p);
    if (!d.workers || !d.start) {
      message.warning('Enter the workers and the start date');
      return;
    }
    save.mutate(
      {
        id: encodeURIComponent(p.process),
        form: {
          workersPlanned: d.workers,
          plannedStart: d.start.format(dateFormat),
          plannedEnd: d.end ? d.end.format(dateFormat) : null,
          overrideReason: d.reason.trim() ? d.reason.trim() : null,
        },
      },
      {
        onSuccess: () => {
          message.success(`${p.process} planned`);
          setDrafts((all) => {
            const next = { ...all };
            delete next[p.process];
            return next;
          });
        },
        onError: (e) =>
          message.error(e?.response?.data?.message ?? 'Save failed'),
      },
    );
  };

  const onRemove = (p: ProderProcessPlan) =>
    remove.mutate(
      { id: encodeURIComponent(p.process) },
      {
        onSuccess: () => message.success(`${p.process} plan removed`),
        onError: (e) =>
          message.error(e?.response?.data?.message ?? 'Remove failed'),
      },
    );

  const columns = [
    {
      title: 'Process',
      dataIndex: 'process',
      render: (v: string, p: ProderProcessPlan) => (
        <Space direction={'vertical'} size={0}>
          <Text strong>{v}</Text>
          <Text type={'secondary'} className={'text-xs'}>
            {workFor(p) === null
              ? 'work content not set up'
              : `${hours(workFor(p))} h ${
                  p.workBasis === 'REMAINING' ? 'remaining (estimated)' : 'total'
                }`}
          </Text>
          {p.areasWithoutFactor.length > 0 && (
            <Tooltip title={p.areasWithoutFactor.join(', ')}>
              <Tag color={'warning'} className={'text-xs'}>
                {p.areasWithoutFactor.length} op(s) without a rate
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Workers',
      render: (_: unknown, p: ProderProcessPlan) => {
        const d = draftOf(p);
        return (
          <Space direction={'vertical'} size={0}>
            <InputNumber
              min={1}
              precision={0}
              value={d.workers ?? undefined}
              onChange={(v) => setDraft(p, { workers: v ?? null })}
              style={{ width: 80 }}
            />
            {!p.saved && (
              <Text type={'secondary'} className={'text-xs'}>
                {p.suggestion.workers === null
                  ? 'allocation not set up'
                  : 'suggested: allocation'}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Start',
      render: (_: unknown, p: ProderProcessPlan) => {
        const d = draftOf(p);
        return (
          <Space direction={'vertical'} size={0}>
            <DatePicker
              value={d.start}
              allowClear={false}
              disabledDate={(c) => c.day() === 0}
              onChange={(v) => setDraft(p, { start: v })}
            />
            {!p.saved && (
              <Text type={'secondary'} className={'text-xs'}>
                suggested: {p.suggestion.startReason}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Days → End',
      render: (_: unknown, p: ProderProcessPlan) => {
        const d = draftOf(p);
        const pv = preview(p, d);
        return (
          <Space direction={'vertical'} size={2}>
            <Text>
              {pv
                ? `${pv.days} day(s) → ${pv.end.format(dateFormat)}`
                : 'not computed'}
            </Text>
            <Text type={'secondary'} className={'text-xs'}>
              {!minutesDay
                ? 'minutes per day not set up'
                : workFor(p) === null
                  ? 'work content not set up'
                  : 'Mon–Sat working days'}
            </Text>
            <DatePicker
              size={'small'}
              placeholder={pv ? 'override end' : 'end date'}
              value={d.end}
              onChange={(v) => setDraft(p, { end: v })}
            />
            {(d.end || !pv) && (
              <Input
                size={'small'}
                placeholder={'reason for the end date'}
                value={d.reason}
                onChange={(e) => setDraft(p, { reason: e.target.value })}
              />
            )}
          </Space>
        );
      },
    },
    {
      title: 'Saved plan',
      render: (_: unknown, p: ProderProcessPlan) =>
        p.saved ? (
          <Space direction={'vertical'} size={0}>
            <Text>
              {convertDate(p.saved.plannedStart, dateFormat)} →{' '}
              {convertDate(p.saved.plannedEnd, dateFormat)}
            </Text>
            <Text type={'secondary'} className={'text-xs'}>
              {p.saved.workersPlanned} workers
              {p.saved.daysNeeded !== null
                ? ` · ${p.saved.daysNeeded} day(s) computed`
                : ''}
              {p.saved.endOverridden ? ' · end set by hand' : ''}
            </Text>
            {p.saved.endOverridden && p.saved.overrideReason && (
              <Text type={'secondary'} className={'text-xs'}>
                reason: {p.saved.overrideReason}
              </Text>
            )}
            <Text type={'secondary'} className={'text-xs'}>
              by {p.saved.plannedBy ?? '—'} · {p.saved.plannedAt ?? ''}
            </Text>
          </Space>
        ) : (
          <Text type={'secondary'}>not planned</Text>
        ),
    },
    {
      title: '',
      render: (_: unknown, p: ProderProcessPlan) => (
        <Space direction={'vertical'} size={4}>
          <Button
            type={'primary'}
            size={'small'}
            loading={save.isPending}
            onClick={() => onSave(p)}
          >
            Save
          </Button>
          {p.saved && (
            <Popconfirm
              title={`Remove the ${p.process} plan?`}
              onConfirm={() => onRemove(p)}
            >
              <Button size={'small'} danger>
                Remove
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const fp = data.finalProder;
  const dueValue = dueDraft ?? (fp.due ? dayjs(fp.due) : null);
  const onSaveDue = () => {
    const d = dueDraft ?? (fp.dueSuggested ? dayjs(fp.dueSuggested) : null);
    if (!d) {
      message.warning('Pick the date the final Proder is due');
      return;
    }
    saveDue.mutate(
      { id: 'final-due', form: { finalDue: d.format(dateFormat), note: null } },
      {
        onSuccess: () => {
          message.success('Final Proder due date saved');
          setDueDraft(null);
        },
        onError: (e) =>
          message.error(e?.response?.data?.message ?? 'Save failed'),
      },
    );
  };

  const lateness = data.daysLateVsRequested;
  return (
    <div className={'space-y-2'}>
      <Space wrap align={'center'}>
        <Text strong>Final Proder</Text>
        {fp.status === 'FINAL' && (
          <Tag color={'green'}>
            received{fp.receivedOn ? ` ${convertDate(fp.receivedOn, dateFormat)}` : ''}
            {fp.revisionNo !== null ? ` · rev ${fp.revisionNo}` : ''}
          </Tag>
        )}
        {fp.status === 'DRAFT' && (
          <Tag color={'orange'}>draft — final Proder not received</Tag>
        )}
        {fp.status === null && <Tag>no PDMOPS record in the sync window</Tag>}
        {fp.revisionCount > 1 && (
          <Text type={'secondary'} className={'text-xs'}>
            {fp.revisionCount} revisions
            {fp.shipDateVersions > 1
              ? ` · ship date changed ${fp.shipDateVersions - 1} time(s)`
              : ''}
          </Text>
        )}
        {fp.receivedLateDays !== null && (
          <Tag color={'red'}>received {fp.receivedLateDays} day(s) after due</Tag>
        )}
        <Text type={'secondary'}>due</Text>
        <DatePicker
          size={'small'}
          value={dueValue}
          placeholder={
            fp.dueSuggested
              ? `suggested ${convertDate(fp.dueSuggested, dateFormat)}`
              : 'not set up'
          }
          onChange={(v) => setDueDraft(v)}
        />
        <Button size={'small'} loading={saveDue.isPending} onClick={onSaveDue}>
          {fp.due ? 'Change due' : fp.dueSuggested ? 'Use / save' : 'Save due'}
        </Button>
        {fp.due && (
          <Text type={'secondary'} className={'text-xs'}>
            set by {fp.dueSetBy ?? '—'} · {fp.dueSetAt ?? ''}
          </Text>
        )}
      </Space>
      {fp.late && (
        <Alert
          type={'error'}
          showIcon
          message={`Final Proder not received and due ${convertDate(
            fp.due as string,
            dateFormat,
          )} has passed — reschedule the plan below`}
        />
      )}
      <Space wrap>
        <Text strong>Plan</Text>
        <Text type={'secondary'}>
          finishes{' '}
          {data.planFinish ? convertDate(data.planFinish, dateFormat) : '—'}
          {' · '}requested ship{' '}
          {data.requestedShipDate
            ? convertDate(data.requestedShipDate, dateFormat)
            : '—'}
          {' · '}ship gap{' '}
          {data.shipGapDays === null ? 'not set up' : `${data.shipGapDays} d`}
        </Text>
        {lateness !== null && (
          <Tag color={lateness > 0 ? 'red' : 'green'}>
            {lateness > 0
              ? `${lateness} day(s) late vs requested`
              : `${-lateness} day(s) to spare`}
          </Tag>
        )}
      </Space>
      <ReadinessBlock api={API} plan={data} />
      <ShipDecisionBlock api={API} plan={data} />
      {data.noRouting && (
        <Text type={'secondary'} className={'text-xs'}>
          No WIP ticket yet: all processes are offered and work content is not
          set up, so give each planned process its end date and a reason.
        </Text>
      )}
      <Table
        size={'small'}
        rowKey={'process'}
        pagination={false}
        dataSource={data.processes}
        columns={columns}
      />
    </div>
  );
};

export default ProderPlanSection;
